/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

/*
 * CentrifugeRealtime - Thin wrapper around a Centrifuge client maintaining a long-lived
 * websocket connection for realtime chat events: new messages, room updates, presence, etc.
 *
 * Responsibility:
 *  - Manage single underlying WS connection lifecycle (connect, disconnect, reconnect)
 *  - Subscribe/unsubscribe to channels (e.g. user, room, global) lazily
 *  - Normalize incoming payloads & re-emit as strongly typed SDK events
 *  - Provide hooks for auth token refresh
 *
 * Core now ships with a direct (hard) dependency on 'centrifuge'. A custom factory can still
 * be provided to override construction. If omitted we instantiate Centrifuge synchronously.
 */

import type { XHubChatClient } from '../../client';

import type { ICentrifugeLike, RawNotiMessage, RealtimeEventHandlerMap, RealtimeMessagePayload, RealtimeOptions, RealtimeRoomUpdatePayload } from './interface';
import { Centrifuge as RealCentrifuge } from 'centrifuge';
import { TypedReEmitter } from '../../emit/ReEmitter';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { RealtimeEvent, RealtimeTransactionType } from './interface';

export class CentrifugeRealtime extends TypedEventEmitter<RealtimeEvent, RealtimeEventHandlerMap> {
  private centrifuge?: ICentrifugeLike;
  private readonly opts: RealtimeOptions;
  private connected = false;
  private reconnectAttempts = 0;
  private activeSubscriptions = new Map<string, { unsubscribe: () => void }>();
  private isRefreshingToken = false;
  private pendingConnectionAfterRefresh = false;
  public readonly reEmitter = new TypedReEmitter<RealtimeEvent, RealtimeEventHandlerMap>(this);

  public constructor(opts: RealtimeOptions, private client: XHubChatClient) {
    super();
    this.opts = { ...opts };
    this.setMaxListeners(0);
  }

  /*
   * Usage (default factory):
   *   const rt = new CentrifugeRealtime({ url: 'wss://realtime.example/ws', initialToken, defaultChannels:['user.@me','presence.global'] });
   *   await rt.connect();
   *   rt.on(RealtimeEvent.NewMessage, m => console.log(m));
   *
   * If you want to inject a custom (mock / wrapped) centrifuge instance, supply
   *   centrifugeFactory: () => myMock
   * and omit url / initialToken. This is heavily used in unit tests where we do
   * not pull in a real websocket implementation.
   *
   * Token Refresh Flow:
   *  - Provide tokenProvider returning Promise<string>.
   *  - On connect() we pre-fetch and set token if available.
   *  - Auto-refresh enabled by default (autoRefreshToken: true).
   *  - On auth errors (401, 403, token-related), automatically calls tokenProvider and refreshes.
   *  - You can call refreshToken() manually if needed.
   *  - Listen to TokenRefreshed event to know when refresh completes.
   *  - Set autoRefreshToken: false to disable auto-refresh and handle manually.
   *
   * Reconnect Strategy: simple linear backoff (attempt * baseDelay) up to max attempts.
   * Auth errors during auto-refresh will attempt reconnection after successful token update.
   */

  /** Ensure underlying centrifuge instance exists (synchronous) */
  private ensureInstance(): ICentrifugeLike {
    if (this.centrifuge) return this.centrifuge;
    let created: ICentrifugeLike;
    if (this.opts.centrifugeFactory) {
      try {
        created = this.opts.centrifugeFactory();
      } catch (e) {
        this.emit(RealtimeEvent.Error, { message: 'centrifugeFactory failed', cause: e });
        throw e;
      }
    } else {
      if (!this.opts.url) {
        const err = new Error('realtime url missing (provide url or centrifugeFactory)');
        this.emit(RealtimeEvent.Error, { message: err.message, cause: err });
        throw err;
      }
      try {
        const raw = new RealCentrifuge(this.opts.url, this.opts.token ? { token: this.opts.token } : {});
        // If mock already provides subscribe signature we can use it directly.
        if (typeof (raw as any).subscribe === 'function' && typeof (raw as any).newSubscription !== 'function') {
          created = raw as unknown as ICentrifugeLike;
        } else {
          // Wrap real Centrifuge (v5) which exposes newSubscription/getSubscription API.
          // Adapter normalizes the API to our ICentrifugeLike.subscribe signature so the rest of
          // the realtime layer does not depend on Centrifuge-specific subscription management.
          // This also lets tests provide a lightweight mock implementing subscribe directly.
          const adapter: ICentrifugeLike = {
            connect: () => raw.connect(),
            disconnect: () => raw.disconnect(),
            on: (event: string, cb: (...args: any[]) => void) => (raw as any).on(event, cb),
            removeListener: (event: string, cb: (...args: any[]) => void) => (raw as any).removeListener(event, cb),
            subscribe: (channel: string, cb: (ctx: { data: any }) => void) => {
              const sub = (raw as any).getSubscription?.(channel) || (raw as any).newSubscription(channel);
              (sub as any).on('publication', (ctx: any) => cb({ data: ctx?.data }));
              try {
                (sub as any).subscribe?.();
              } catch {
                // ignore subscribe errors (e.g., already subscribed)
              }
              return {
                unsubscribe: () => {
                  try {
                    (sub as any).unsubscribe?.();
                  } catch {
                    // ignore unsubscribe errors
                  }
                },
              };
            },
            setToken: (token: string) => {
              try {
                raw.setToken(token);
              } catch (err) {
                this.emit(RealtimeEvent.Error, { message: 'setToken failed', cause: err });
              }
            },
          };
          created = adapter;
        }
      } catch (e) {
        this.emit(RealtimeEvent.Error, { message: 'centrifuge construct failed', cause: e });
        throw e;
      }
    }
    this.centrifuge = created;
    this.bindCoreEvents(created);
    return created;
  }

  private bindCoreEvents(cf: ICentrifugeLike) {
    cf.on('connected', (ctx: any) => {
      this.connected = true;
      this.reconnectAttempts = 0;
      this.pendingConnectionAfterRefresh = false;
      this.emit(RealtimeEvent.Connected, {
        session: ctx?.session || ctx?.client || 'unknown',
        transport: ctx?.transport || 'websocket',
      });

      // subscribe defaults
      for (const ch of this.opts.channels || []) this.subscribe(ch);
    });

    cf.on('disconnected', (ctx: any) => {
      this.connected = false;
      this.emit(RealtimeEvent.Disconnected, ctx?.reason || 'unknown');
      // Only schedule reconnect if we're not in the middle of a token refresh
      if (!this.isRefreshingToken) {
        this.scheduleReconnect();
      }
    });

    cf.on('error', (err: any) => {
      // Check if this is a token-related error and handle it automatically
      if (this.isTokenExpiredError(err) && this.opts.autoRefreshToken !== false) {
        this.handleAuthError(err);
      } else {
        this.emit(RealtimeEvent.Error, { message: err?.message || 'realtime error', cause: err });
      }
    });
  }

  public async connect(): Promise<void> {
    // Check if already connected to avoid multiple connections
    if (this.connected) {
      this.opts.logger?.debug?.('Already connected, skipping connect call');
      return;
    }

    const cf = this.ensureInstance();
    if (this.opts.tokenProvider) {
      try {
        const token = await this.opts.tokenProvider();
        cf.setToken?.(token);
      } catch (e) {
        this.emit(RealtimeEvent.Error, { message: 'token fetch failed', cause: e });
      }
    }
    cf.connect();
  }

  public disconnect(): void {
    this.reconnectAttempts = 0;
    this.connected = false;
    if (this.centrifuge) {
      try {
        this.centrifuge.disconnect();
      } catch (e) {
        this.emit(RealtimeEvent.Error, { message: 'disconnect error', cause: e });
      }
    }
  }

  /** Subscribe to a logical channel. Returns true if new subscription created. */
  public subscribe(channel: string): boolean {
    if (this.activeSubscriptions.has(channel)) return false;
    const cf = this.ensureInstance();
    const sub = cf.subscribe(channel, (ctx: { data: any }) => this.handleChannelMessage(channel, ctx?.data));
    this.activeSubscriptions.set(channel, sub);
    this.emit(RealtimeEvent.ChannelSubscribed, channel);
    return true;
  }

  /** Unsubscribe from a channel. */
  public unsubscribe(channel: string): boolean {
    const sub = this.activeSubscriptions.get(channel);
    if (!sub) return false;
    try {
      sub.unsubscribe();
    } catch (e) {
      this.emit(RealtimeEvent.Error, { message: 'unsubscribe failed', cause: e });
    }
    this.activeSubscriptions.delete(channel);
    this.emit(RealtimeEvent.ChannelUnsubscribed, channel);
    return true;
  }

  /**
   * Simple channel classification heuristic - adjust as needed.
   * @param channel - channel name
   * @param raw - raw payload
   * @returns - one of 'message' | 'room' | 'presence' | 'generic'
   */
  private classify(channel: string, raw: RawNotiMessage) {
    const type = raw.transaction_type;
    if (type === RealtimeTransactionType.Chat || type === RealtimeTransactionType.Comment) return 'message';
    if (type === 'post_notification') return 'room';
    if (channel === 'online:index') return 'presence';
    return 'generic';
  }

  private handleChannelMessage(channel: string, raw: RawNotiMessage) {
    // Always emit raw notification for consumers who want to handle it themselves
    this.emit(RealtimeEvent.NewNotification, raw);

    const kind = this.classify(channel, raw);
    switch (kind) {
      case 'message': {
        const mapped = this.opts.mapMessage?.(raw) ?? (raw as unknown as RealtimeMessagePayload);
        if (mapped) this.emit(RealtimeEvent.NewMessage, mapped);
        break;
      }
      case 'presence': {
        const mapped = this.opts.mapPresence?.(raw) ?? (raw as unknown as Record<string, boolean>);
        if (mapped) this.emit(RealtimeEvent.Presence, mapped);
        break;
      }
      case 'room': {
        const mapped = this.opts.mapRoomUpdate?.(raw) ?? (raw as unknown as RealtimeRoomUpdatePayload);
        if (mapped) this.emit(RealtimeEvent.RoomUpdated, mapped);
        break;
      }
      default: {
        // Unclassified; ignore or log
        this.opts.logger?.debug?.('Unhandled channel payload', channel, raw);
      }
    }
  }

  private scheduleReconnect() {
    const max = this.opts.maxReconnectAttempts ?? 5;
    if (this.connected) return; // already reconnected
    if (this.reconnectAttempts >= max) return;
    this.reconnectAttempts += 1;
    this.emit(RealtimeEvent.Reconnecting, this.reconnectAttempts);
    const delay = (this.opts.reconnectBaseDelayMs ?? 1000) * this.reconnectAttempts;
    setTimeout(() => {
      if (this.connected) return;
      this.connect().catch(err => this.emit(RealtimeEvent.Error, { message: 'reconnect failed', cause: err }));
    }, delay);
  }

  /** Refresh auth token manually (e.g., after 401) */
  public async refreshToken(): Promise<void> {
    if (!this.centrifuge?.setToken || !this.opts.tokenProvider) return;

    this.isRefreshingToken = true;
    try {
      const token = await this.opts.tokenProvider();
      this.centrifuge.setToken(token);
      this.opts.logger?.debug?.('Token refreshed successfully');
      this.emit(RealtimeEvent.TokenRefreshed);

      // If there was a pending connection after refresh, try to connect
      if (this.pendingConnectionAfterRefresh) {
        this.pendingConnectionAfterRefresh = false;
        await this.connect();
      }
    } catch (e) {
      this.emit(RealtimeEvent.Error, { message: 'token refresh failed', cause: e });
      throw e;
    } finally {
      this.isRefreshingToken = false;
    }
  }

  /**
   * Check if an error is related to token expiration or authentication failure
   */
  private isTokenExpiredError(error: any): boolean {
    if (!error) return false;

    const errorCode = error.code || error.status;
    const errorMessage = (error.message || '').toLowerCase();

    // Check for common authentication error patterns
    return (
      errorCode === 401
      || errorCode === 403
      || errorMessage.includes('token')
      || errorMessage.includes('unauthorized')
      || errorMessage.includes('forbidden')
      || errorMessage.includes('authentication')
      || errorMessage.includes('expired')
    );
  }

  /**
   * Handle authentication errors with automatic token refresh
   */
  private async handleAuthError(error: any): Promise<void> {
    if (!this.opts.tokenProvider || this.isRefreshingToken) {
      // Can't refresh or already refreshing, emit error
      this.emit(RealtimeEvent.Error, {
        message: 'Authentication failed and no token provider available',
        cause: error,
      });
      return;
    }

    this.opts.logger?.debug?.('Detected token expiration, attempting auto-refresh...');

    try {
      await this.refreshToken();
      this.opts.logger?.debug?.('Token auto-refresh completed, reconnecting...');

      // Reconnect after successful token refresh
      if (!this.connected) {
        this.pendingConnectionAfterRefresh = true;
        await this.connect();
      }
    } catch (refreshError) {
      this.opts.logger?.error?.('Auto token refresh failed:', refreshError);
      this.emit(RealtimeEvent.Error, {
        message: 'Auto token refresh failed',
        cause: refreshError,
      });
    }
  }
}

export type { RealtimeMessagePayload as CentrifugeMessage, RealtimeRoomUpdatePayload as CentrifugeRoomUpdate };
